import { Cell, Row, TableEditorConfig, Selection } from "../dto";
import { generateCellRectsByGrid, generateGrid } from "./transform";

/**
 * 根据表格配置和起止cellId，返回一个包含所有选中单元格id的数组
 *
 * @param config - 表格配置对象
 * @param startCellId - 起始单元格id
 * @param endCellId - 结束单元格id
 */
export const generateSelection = (
  config: TableEditorConfig,
  startCellId: string,
  endCellId: string
) => {
  const grid = generateGrid(config);
  const rects = generateCellRectsByGrid(grid);
  const startRect = rects[startCellId];
  const endRect = rects[endCellId];
  // 找到开始和结束单元格的位置
  let startRow = Math.min(startRect.minRow, endRect.minRow),
    startCol = Math.min(startRect.minCol, endRect.minCol),
    endRow = Math.max(startRect.maxRow, endRect.maxRow),
    endCol = Math.max(startRect.maxCol, endRect.maxCol);
  // 开始外扩（如果需要的话）
  while (true) {
    // 每一次外扩时设置当前区域
    let areaStartRow = startRow,
      areaStartCol = startCol,
      areaEndRow = endRow,
      areaEndCol = endCol;
    for (let i = startRow; i <= endRow; i++) {
      for (let j = startCol; j <= endCol; j++) {
        // 跳过内部的单元格，只处理边界
        if (i > startRow && i < endRow && j > startCol && j < endCol) {
          continue;
        }
        const id = grid[i][j];
        const { minRow, minCol, maxRow, maxCol } = rects[id];
        // 更新当前一次外扩边界
        areaStartRow = Math.min(areaStartRow, minRow);
        areaStartCol = Math.min(areaStartCol, minCol);
        areaEndRow = Math.max(areaEndRow, maxRow);
        areaEndCol = Math.max(areaEndCol, maxCol);
      }
    }
    // 如果没有变化，说明已经没有可以外扩的边界了，跳出循环
    if (
      areaStartRow === startRow &&
      areaStartCol === startCol &&
      areaEndRow === endRow &&
      areaEndCol === endCol
    ) {
      break;
    }
    // 更新边界
    startRow = areaStartRow;
    startCol = areaStartCol;
    endRow = areaEndRow;
    endCol = areaEndCol;
  }
  const set = new Set<string>();
  for (let i = startRow; i <= endRow; i++) {
    for (let j = startCol; j <= endCol; j++) {
      set.add(grid[i][j]);
    }
  }
  return {
    cellIds: Array.from(set),
    rect: {
      minRow: startRow,
      minCol: startCol,
      maxRow: endRow,
      maxCol: endCol,
      startId: grid[startRow][startCol],
      endId: grid[endRow][endCol],
    },
  };
};

/**
 * 根据选区获取选区中所有单元格信息
 * @param config - 表格配置
 * @param selection - 选区
 * @returns 单元格信息集合
 */
export const getCellsBySelection = (
  config: TableEditorConfig,
  selection: Selection
) => {
  const { rows } = config;
  const { cellIds } = selection;
  const ret: { cell: Cell; rowIndex: number; colIndex: number }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const { cells } = rows[i];
    for (let j = 0; j < cells.length; j++) {
      if (cellIds.includes(cells[j].id)) {
        ret.push({ cell: cells[j], rowIndex: i, colIndex: j });
      }
    }
  }
  return ret;
};
/**
 * 根据选区获取选区中所有单元格信息
 * @param config - 表格配置
 * @param selection - 选区
 * @returns 单元格信息集合
 */
export const getRowsBySelection = (
  config: TableEditorConfig,
  selection: Selection
) => {
  const { rows } = config;
  const { rect: { minRow, maxRow } = {} } = selection;
  const ret: Row[] = [];
  for (let i = minRow; i <= maxRow; i++) {
    ret.push(rows[i]);
  }
  return ret;
};
